import { DialogController, DialogService } from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';
import './mooting-create-offer-dialog.scss';
import { ValidationController, Validator } from 'aurelia-validation';
import { Router } from 'aurelia-router';
import { StandardValidationBuilder } from 'lib/validation';
import { MooringOfferService, MooringService } from 'services/mooring';
import {
  BuoyDrawingPropertyDto,
  CreateMooringOfferDto,
  CreateMooringSiteSetupDto,
  MooringOfferChangeDifferenceDto,
  MooringOfferChangeImpactDto,
  MooringServiceCorridorDirection,
  MooringStationListItem,
} from 'models/mooring';
import { AsyncErrorHandler } from 'lib/ui';
import { CustomerService } from 'services/customer-service';
import { SiteService } from 'services/site-service';
import { AnalysisCompanyService, MooringStationService, ToastService } from 'services';
import { I18N } from 'aurelia-i18n';
import { PubSub } from 'lib/event/PubSub';
import { ContactService } from 'services/contact-service';
import { CustomerDialog } from 'features/contact/customer-dialog';
import { UserService } from 'services/user-service';
import { UserModels } from 'models/UserModels';
import { connectTo } from 'aurelia-store';
import { ApplicationState } from 'lib/state';
import { Models } from 'models/core';
import { DeleteDialogService } from 'services/delete-dialog-service';
import { cleanDmsFormat, isDmsGpsPosition } from 'lib/helpers';
import { CustomerLocalityDialog } from 'features/locality/locality-dialog';
import { renderMap } from 'views/mooring/mooring-map';
import { AnalysisCompanyDialog } from 'features/analysis-company/analysis-company-dialog';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';

@autoinject
@connectTo()
export class MooringCreateOfferDialog {
  protected model: CreateMooringOfferDto;

  protected offerId?: number;

  private state!: ApplicationState; // Injected by @connectTo, do not remove or change type

  protected customers: Models.Customer[] = [];

  protected hasFleet = false;
  protected hasOnlyFleet = false;
  protected hasFacility = true;

  protected useRotationMode = false;
  protected isHorizontal: boolean = true;

  private originalValues: MooringOfferChangeDifferenceDto = {};

  protected positionOrder = [
    ['CenterPositionLat', 'CenterPositionLong'],
    ['NWPositionLat', 'NWPositionLong'],
    ['NEPositionLat', 'NEPositionLong'],
    ['SEPositionLat', 'SEPositionLong'],
    ['SWPositionLat', 'SWPositionLong'],
  ];

  constructor(
    private dialogSerivce: DialogService,
    private dialogControl: DialogController,
    private ruleBuilder: StandardValidationBuilder,
    private toastService: ToastService,
    protected customerService: CustomerService,
    protected siteService: SiteService,
    protected validationController: ValidationController,
    protected validator: Validator,
    protected mooringOfferService: MooringOfferService,
    protected mooringService: MooringService,
    private contactService: ContactService,
    private router: Router,
    private t: I18N,
    private pubSub: PubSub,
    private analysisCompanyService: AnalysisCompanyService,
    private userService: UserService,
    private mooringStationService: MooringStationService,
    private deleteDialogService: DeleteDialogService,
    private confirmDialog: ConfirmDialog,
  ) {
    this.model = new CreateMooringOfferDto();
    this.model.SiteSetup = new CreateMooringSiteSetupDto();
    this.model.SiteSetup.ReliabilityClass = 2;
    this.model.SiteSetup.ServiceCorridorDirection = MooringServiceCorridorDirection.Horizontal;
    this.model.SiteSetup.ServiceCorridorPlacement = 1;
    this.model.SiteSetup.ServiceCorridorWidth = 100;
    this.model.SiteSetup.StartBuoyPosition = 'NW';
    this.createValidationRules();
  }

  protected applyTestData() {
    this.createValidationRules();
  }

  @AsyncErrorHandler
  protected async getOffer(id: number) {
    const { data: offer } = await this.mooringOfferService.getOffer(id);

    this.model.Title = offer.Title;

    this.model.Hs = offer.Hs;
    this.model.Vc = offer.Vc;

    this.model.CustomerId = offer.CustomerId;
    this.model.ContactId = offer.ContactId;
    this.model.SiteNumber = offer.SiteNumber;
    this.model.SiteId = offer.SiteId;
    this.model.SalesPersonId = offer.SalesPersonId;

    this.model.CrmReference = offer.CrmReference;
    this.model.AnalysisReference = offer.AnalysisReference;
    this.model.ProductionLocationId = offer.ProductionLocationId;

    this.model.AnalysisCompanyId = offer.AnalysisCompanyId;

    this.hasOnlyFleet = offer.HasOnlyFleet;

    this.model.SiteSetup.NoBridles = offer.SiteSetup.NoBridles;
    this.originalValues.NoBridlesOld = offer.SiteSetup.NoBridles;
    this.model.SiteSetup.ReliabilityClass = offer.SiteSetup.ReliabilityClass;

    this.model.SiteSetup.NoAnchorLines = offer.SiteSetup.NoAnchorLines;
    this.originalValues.NoAnchorLinesOld = offer.SiteSetup.NoAnchorLines;
    this.model.SiteSetup.NoFleetAnchorLines = offer.SiteSetup.NoFleetAnchorLines;
    this.model.SiteSetup.GridLineLength = offer.SiteSetup.GridLineLength;

    this.model.SiteSetup.HasServiceCorridor = offer.SiteSetup.HasServiceCorridor;
    this.model.SiteSetup.ServiceCorridorDirection = offer.SiteSetup.ServiceCorridorDirection;
    this.model.SiteSetup.ServiceCorridorPlacement = offer.SiteSetup.ServiceCorridorPlacement;
    this.model.SiteSetup.ServiceCorridorWidth = offer.SiteSetup.ServiceCorridorWidth;

    this.model.SiteSetup.NoCagesWidth = offer.SiteSetup.NoCagesWidth;
    this.originalValues.NoCagesWidthOld = offer.SiteSetup.NoCagesWidth;
    this.model.SiteSetup.NoCagesHeight = offer.SiteSetup.NoCagesHeight;
    this.originalValues.NoCagesHeightOld = offer.SiteSetup.NoCagesHeight;
    this.model.SiteSetup.MooringDepth = offer.SiteSetup.MooringDepth;

    this.model.SiteSetup.NEPositionLat = offer.SiteSetup.NEPositionLat;
    this.model.SiteSetup.NEPositionLong = offer.SiteSetup.NEPositionLong;
    this.model.SiteSetup.SEPositionLat = offer.SiteSetup.SEPositionLat;
    this.model.SiteSetup.SEPositionLong = offer.SiteSetup.SEPositionLong;
    this.model.SiteSetup.NWPositionLat = offer.SiteSetup.NWPositionLat;
    this.model.SiteSetup.NWPositionLong = offer.SiteSetup.NWPositionLong;
    this.model.SiteSetup.SWPositionLat = offer.SiteSetup.SWPositionLat;
    this.model.SiteSetup.SWPositionLong = offer.SiteSetup.SWPositionLong;

    this.model.SiteSetup.FleetAngleFromNorth = offer.SiteSetup.FleetAngleFromNorth;

    this.model.SiteSetup.CenterPositionLong = offer.SiteSetup.CenterPositionLong;
    this.model.SiteSetup.CenterPositionLat = offer.SiteSetup.CenterPositionLat;

    this.model.SiteSetup.SiteAngleFromNorth = offer.SiteSetup.SiteAngleFromNorth;

    this.model.SiteSetup.FleetCenterPositionLong = offer.SiteSetup.FleetCenterPositionLong;
    this.model.SiteSetup.FleetCenterPositionLat = offer.SiteSetup.FleetCenterPositionLat;

    this.hasFacility = this.model.SiteSetup.NoCagesWidth + this.model.SiteSetup.NoCagesHeight > 0;

    // Detect which positioning mode to use based on available data
    this.useRotationMode = !!(
      offer.SiteSetup.SiteAngleFromNorth !== null &&
      offer.SiteSetup.SiteAngleFromNorth !== undefined &&
      !offer.SiteSetup.NWPositionLat
    );
    this.hasFleet = this.model.SiteSetup.NoFleetAnchorLines > 0;

    this.isHorizontal = offer.SiteSetup.IsHorizontal ?? true;
    this.model.SiteSetup.IsHorizontal = this.isHorizontal;

    setTimeout(() => {
      this.createExampleGrid();
    }, 100);
  }

  protected activate(args?: { id?: number }) {
    if (args?.id) {
      this.offerId = args.id;
      void this.getOffer(args.id);
    }
  }

  protected async attached() {
    await this.loadAllCustomers();
    // If we are  not editing, assign the current user as seller.
    if (!this.offerId) {
      this.model.SalesPersonId = this.state.user.Id;
      return;
    }
  }

  @AsyncErrorHandler
  async loadAllCustomers() {
    this.customers = await this.customerService.getAll(`?$select=Name,Id`);
  }

  protected cleanGps() {
    setCleanDmsFormat('FleetCenterPositionLat', this.model.SiteSetup);
    setCleanDmsFormat('FleetCenterPositionLong', this.model.SiteSetup);

    setCleanDmsFormat('CenterPositionLat', this.model.SiteSetup);
    setCleanDmsFormat('CenterPositionLong', this.model.SiteSetup);
    setCleanDmsFormat('NWPositionLat', this.model.SiteSetup);
    setCleanDmsFormat('NWPositionLong', this.model.SiteSetup);
    setCleanDmsFormat('NEPositionLat', this.model.SiteSetup);
    setCleanDmsFormat('NEPositionLong', this.model.SiteSetup);
    setCleanDmsFormat('SWPositionLat', this.model.SiteSetup);
    setCleanDmsFormat('SWPositionLong', this.model.SiteSetup);
    setCleanDmsFormat('SEPositionLat', this.model.SiteSetup);
    setCleanDmsFormat('SEPositionLong', this.model.SiteSetup);
  }

  @AsyncErrorHandler
  async onSave() {
    this.cleanGps();

    const { valid } = await this.validationController.validate();
    if (!valid) return;

    if (this.offerId) {
      const cagesWidthChanged = this.model.SiteSetup.NoCagesWidth.toString() !== this.originalValues.NoCagesWidthOld?.toString();
      const cagesHeightChanged = this.model.SiteSetup.NoCagesHeight.toString() !== this.originalValues.NoCagesHeightOld?.toString();
      const anchorLinesChanged = this.model.SiteSetup.NoAnchorLines?.toString() !== this.originalValues.NoAnchorLinesOld?.toString();
      const bridlesChanged = this.model.SiteSetup.NoBridles.toString() !== this.originalValues.NoBridlesOld?.toString();

      if (cagesWidthChanged || cagesHeightChanged || anchorLinesChanged || bridlesChanged) {
        let confirmChangeWithConsequencesText = await this.getconfirmChangeWithConsequencesText(this.offerId);
        const confirm = await this.confirmDialog.confirmYesCancel(this.t.tr('dialog.areYouSure'), confirmChangeWithConsequencesText);
        if (!confirm) return;

        this.model.NoBridlesOld = this.originalValues.NoBridlesOld;
        this.model.NoBridlesNew = this.model.SiteSetup.NoBridles;
      }

      await this.mooringOfferService.updateOffer(this.offerId, this.model);
      this.toastService.showSuccess(this.t.tr('general.updated'));
      this.pubSub.publish('entity:updated', null);
    } else {
      this.model.CreateOnlyFleet = this.hasOnlyFleet;

      const { data } = await this.mooringOfferService.createOffer(this.model);

      this.model.CreateFacility = this.hasFacility;
      this.model.CreateFleet = this.hasFleet;

      this.toastService.showSuccess(this.t.tr('general.created'));
      this.router.navigate(`mooring/offers/${data.Id}`);
    }
    this.onCancel();
  }

  onCancel() {
    void this.dialogControl.cancel();
  }

  protected async getCustomerInfo(id: number) {
    const customer = await this.customerService.get(id);
    return {
      id: customer.Id,
      title: customer.Name,
    };
  }

  protected getBuoysCount(input: number | undefined | string, direction: MooringServiceCorridorDirection) {
    if (input === undefined || input === null) return 0;

    if (typeof input === 'string') {
      const num = +input;
      if (isNaN(num)) return 0;
      input = num;
    }

    const hasServiceCorridor = this.model.SiteSetup.HasServiceCorridor;
    if (hasServiceCorridor && this.model.SiteSetup.ServiceCorridorDirection == direction) {
      return input + 2;
    }

    return input + 1;
  }

  buoys: BuoyDrawingPropertyDto[] = [];
  protected createExampleGrid(resetMap = true, startBuoy: { row: number; column: number } = { row: 1, column: 1 }) {
    const buoys: BuoyDrawingPropertyDto[] = [];
    const noCagesHeight = this.getBuoysCount(this.model.SiteSetup.NoCagesHeight, MooringServiceCorridorDirection.Horizontal);
    const noCagesWidth = this.getBuoysCount(this.model.SiteSetup.NoCagesWidth, MooringServiceCorridorDirection.Vertical);
    const gridRows = this.isHorizontal ? noCagesHeight : noCagesWidth;
    const gridColumns = this.isHorizontal ? noCagesWidth : noCagesHeight;
    dbg.pritty('Cages pre:', { noCagesHeight, noCagesWidth });
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    dbg.pritty('Cages post:', { noCagesHeight, noCagesWidth, gridRows, gridColumns, startBuoy });

    for (let row = 1; row <= gridRows; row++) {
      for (let column = 1; column <= gridColumns; column++) {
        let letterIndex = 0;
        if (this.isHorizontal) {
          // Horizontal mode: letters follow rows
          if (startBuoy.row == 1) {
            // Subtract 1 to get zero based index for alphabet selection
            letterIndex = row - 1;
          } else {
            // Starting from last row, reverse the lettering.
            letterIndex = gridRows - row;
          }
        } else {
          // Vertical mode: letters follow columns
          if (startBuoy.column == 1) {
            letterIndex = column - 1;
          } else {
            letterIndex = gridColumns - column;
          }
        }

        let number = 0;
        if (this.isHorizontal) {
          // Horizontal mode: numbers follow columns
          if (startBuoy.column == 1) {
            number = column;
          } else {
            // Starting from last column, reverse the numbering, eg 5 becomes 1, 4 becomes 2
            // Add 1 to make it not zero based
            number = gridColumns - column + 1;
          }
        } else {
          // Vertical mode: numbers follow rows
          if (startBuoy.row == 1) {
            number = row;
          } else {
            number = gridRows - row + 1;
          }
        }

        const isAtStartBuoy = row === startBuoy.row && column === startBuoy.column;

        const no = alphabet[letterIndex] + number;
        buoys.push({
          Row: row,
          AnalysisNo: no,
          MooringNo: no,
          Column: column,
          Id: (row - 1) * gridColumns + column,
          IsLightBuoy: isAtStartBuoy,
        });
      }
    }

    this.buoys = buoys;
    this.render2(resetMap);
  }

  protected render2(resetMap = true) {
    const serviceCorridorConfig =
      this.model.SiteSetup.HasServiceCorridor && this.model.SiteSetup.ServiceCorridorDirection
        ? ({
            Direction: this.model.SiteSetup.ServiceCorridorDirection || MooringServiceCorridorDirection.Horizontal,
            Placement: +(this.model.SiteSetup.ServiceCorridorPlacement || 1),
            Width: this.model.SiteSetup.ServiceCorridorWidth || (this.model.SiteSetup.GridLineLength || 100) * 2,
          } as const)
        : undefined;

    renderMap(
      {
        MooringId: 0,
        GridLineLength: +this.model.SiteSetup.GridLineLength || 100,
        PenCircumference: 10,
        SiteAngleFromNorth: this.model.SiteSetup.SiteAngleFromNorth || 0,
        FleetAngleFromNorth: 0,
        Buoys: this.buoys,
        Cages: [],
        Bridles: [],
        GridLines: [],
        AnchorLines: [],
        FleetAnchorLines: [],
        ServiceCorridor: serviceCorridorConfig,
        NWPositionLat: this.model.SiteSetup.NWPositionLat,
        NWPositionLong: this.model.SiteSetup.NWPositionLong,
        NEPositionLat: this.model.SiteSetup.NEPositionLat,
        NEPositionLong: this.model.SiteSetup.NEPositionLong,
        SWPositionLat: this.model.SiteSetup.SWPositionLat,
        SWPositionLong: this.model.SiteSetup.SWPositionLong,
        SEPositionLat: this.model.SiteSetup.SEPositionLat,
        SEPositionLong: this.model.SiteSetup.SEPositionLong,
        CenterPositionLat: this.model.SiteSetup.CenterPositionLat,
        CenterPositionLong: this.model.SiteSetup.CenterPositionLong,
        FleetCenterPositionLat: this.hasFleet ? this.model.SiteSetup.FleetCenterPositionLat : undefined,
        FleetCenterPositionLong: this.hasFleet ? this.model.SiteSetup.FleetCenterPositionLong : undefined,
      },
      {
        onFeatureClick: (e) => {
          const b = this.buoys.find((x) => x.Id === e.id);
          if (!b) return;

          const startBuoy = {
            row: 1,
            column: 1,
          };

          startBuoy.row = b.Row;
          startBuoy.column = b.Column;

          const noCagesHeight = this.getBuoysCount(this.model.SiteSetup.NoCagesHeight, MooringServiceCorridorDirection.Horizontal);
          const noCagesWidth = this.getBuoysCount(this.model.SiteSetup.NoCagesWidth, MooringServiceCorridorDirection.Vertical);
          const gridRows = this.isHorizontal ? noCagesHeight : noCagesWidth;
          const gridColumns = this.isHorizontal ? noCagesWidth : noCagesHeight;

          if (startBuoy.row !== 1 && startBuoy.row !== gridRows) return;
          if (startBuoy.column !== 1 && startBuoy.column !== gridColumns) return;

          if (startBuoy.row === 1 && startBuoy.column === 1) {
            this.model.SiteSetup.StartBuoyPosition = 'NW';
          } else if (startBuoy.row === 1 && startBuoy.column === gridColumns) {
            this.model.SiteSetup.StartBuoyPosition = 'NE';
          } else if (startBuoy.row === gridRows && startBuoy.column === gridColumns) {
            this.model.SiteSetup.StartBuoyPosition = 'SE';
          } else if (startBuoy.row === gridRows && startBuoy.column === 1) {
            this.model.SiteSetup.StartBuoyPosition = 'SW';
          }

          this.createExampleGrid(false, startBuoy);
        },
        zoom: 16,
        resetMap,
        containerId: 'preview-map',
        renderOptions: { showAnalysisNumbering: true, largeBuoys: true },
      }
    );
  }

  protected findCustomer(searchText: string) {
    return this.customers
      .filter((c) => c.Name.toLowerCase().includes(searchText.toLowerCase()))
      .map((c) => ({
        id: c.Id,
        title: c.Name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  protected customerSelected(customer: { id: number }) {
    this.model.CustomerId = customer.id;
    this.model.ContactId = undefined;
  }

  protected async getContactInfo(id: number) {
    const contact = await this.contactService.get(id);
    return {
      id: contact.Id,
      title: contact.Name,
    };
  }

  protected async findContact(searchText: string) {
    const contacts = await this.contactService.getAll(`?$filter=CustomerId eq ${this.model.CustomerId}`);
    return contacts
      .filter((c) => c.Name.toLowerCase().includes(searchText.toLowerCase()))
      .map((c) => ({
        id: c.Id,
        title: c.Name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  protected contactSelected(contact: { id: number }) {
    this.model.ContactId = contact.id;
  }

  @AsyncErrorHandler
  protected async getAnalysisCompanyInfo(id: number) {
    if (!id) return undefined;
    const { data: analysisCompany } = await this.analysisCompanyService.getCompany(id);
    if (!analysisCompany) return undefined;
    return {
      id: analysisCompany.Id,
      title: analysisCompany.Name,
    };
  }

  @AsyncErrorHandler
  protected async findAnalysisCompany(searchText: string) {
    const { data: analysisCompanys } = await this.analysisCompanyService.getCompanies();
    if (!analysisCompanys || !analysisCompanys.length) return [];
    return analysisCompanys
      .filter((c) => c.Name.toLowerCase().includes(searchText.toLowerCase()))
      .map((c) => ({
        id: c.Id,
        title: c.Name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  protected analysisCompanySelected(analysisCompany: { id: number }) {
    this.model.AnalysisCompanyId = analysisCompany.id;
  }

  @AsyncErrorHandler
  protected async getSellerInfo(id: string) {
    if (!id) return undefined;
    const { data } = await this.userService.getMooringSellers();
    if (!data || !data.length) return undefined;
    const seller = data.find((x) => x.Id == id);

    if (!seller) return undefined;

    return {
      id: seller.Id,
      title: seller.Name,
    };
  }

  private sellers: UserModels.UserSimple[] = [];

  @AsyncErrorHandler
  protected async findSeller(searchText: string) {
    if (!this.sellers?.length) {
      const { data: sellers } = await this.userService.getMooringSellers();
      if (!sellers || !sellers.length) return [];
      this.sellers = sellers;
    }
    return this.sellers
      .filter((c) => c.Name.toLowerCase().includes(searchText.toLowerCase()))
      .map((c) => ({
        id: c.Id,
        title: c.Name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  protected sellerSelected(seller: { id: string }) {
    this.model.SalesPersonId = seller.id;
  }

  @AsyncErrorHandler
  protected async getSiteInfo(id: number) {
    const site = await this.siteService.get(id);
    return {
      id: site.Id,
      title: site.Name,
      hs: site.Hs,
      vc: site.Vc,
    };
  }

  protected async findSite(searchText: string) {
    let customerIdFilter = '';
    if (this.model.CustomerId) {
      customerIdFilter = ` AND CustomerId eq ${this.model.CustomerId}`;
    }
    const sites = await this.siteService.getAll(`?$filter=contains(toLower(Name), '${searchText}')${customerIdFilter}`);
    return sites
      .filter((s) => s.Name.toLowerCase().includes(searchText.toLowerCase()))
      .map((s) => ({
        id: s.Id,
        title: s.Name,
        returns: s.CustomerId,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  protected async siteSelected(site: { id: number; returns: number }) {
    this.model.SiteId = site.id;
    if (site.returns && this.model.CustomerId != site.returns) {
      this.customerSelected({ id: site.returns });
    }

    if (site.id) {
      const selectedSite = await this.getSiteInfo(site.id);
      this.model.Hs = Number(selectedSite.hs);
      this.model.Vc = Number(selectedSite.vc);
    }
  }

  protected confirmBeforeDeleteMooringOffer() {
    void this.deleteDialogService.confirmBeforeDelete(
      () => {
        void this.deleteMooringOffer();
      },
      'dialog.deleteMooringOfferHeading',
      'dialog.deleteOfferAreYouSure',
      'dialog.deleteOffer'
    );
  }

  protected columnRows(data: string) {
    // Ensure its not just whitespace
    data = data?.trim();
    if (!data) return [];

    // Create rows by splitting on new lines
    const rows = data.split('\n').filter(Boolean);

    const mapped = rows.map((row) => {
      return row.split(/\s{2,}/g).map((col) => col.trim());
    });

    return mapped;
  }

  protected pasteValue(event: ClipboardEvent, latOrLong: string, index: number) {
    event.stopPropagation();
    const data = event.clipboardData?.getData('text');
    if (!data) return;

    const table = this.columnRows(data);
    const maxRows = Math.min(table.length, this.positionOrder.length);
    for (let i = 0; i < maxRows; i++) {
      const row = table[i];
      const keys = this.positionOrder[index];
      // If there are no more keys just break out of the loop
      if (!keys || !keys.length) break;

      const keyStart = latOrLong === 'lat' ? 0 : 1;
      // We want to get the data from the first column of the pasted data, and go +1
      let dataColumn = 0;
      for (let j = keyStart; j < keys.length; j++) {
        const key = keys[j] as keyof CreateMooringSiteSetupDto[];
        this.model.SiteSetup[key] = cleanDmsFormat(row[dataColumn]);
        dataColumn++;
      }
      index++;
    }
  }

  protected pasteValueFleet(event: ClipboardEvent, fields: 'FleetCenterPositionLat' | 'FleetCenterPositionLong') {
    event.stopPropagation();
    const data = event.clipboardData?.getData('text');
    if (!data) return;

    const table = this.columnRows(data);

    if (fields === 'FleetCenterPositionLat' || fields === 'FleetCenterPositionLong') {
      const pos = cleanDmsFormat(table[0][0]);
      if (!pos) return;
      this.model.SiteSetup[fields] = pos;
    }
  }

  private async deleteMooringOffer() {
    if (!this.offerId) return;
    await this.mooringOfferService.deleteOffer(this.offerId);
    void this.dialogControl.cancel();
    this.toastService.showSuccess('mooring.mooringOfferDeleted');
    this.router.navigateToRoute('mooring-offers-view');
  }

  protected newCustomer() {
    void this.dialogSerivce
      .open({
        viewModel: CustomerDialog,
        position: () => {
          return {
            top: 0,
          };
        },
      })
      .whenClosed(async ({ output }) => {
        if (!output?.customerId) return;
        await this.loadAllCustomers();
        this.model.CustomerId = output.customerId;
      });
  }

  protected newLocality() {
    void this.dialogSerivce
      .open({
        viewModel: CustomerLocalityDialog,
        model: {
          customerId: this.model.CustomerId,
        },
        position: () => {
          return {
            top: 0,
          };
        },
      })
      .whenClosed(async ({ output }) => {
        if (!output?.siteId) return;
        await this.loadAllCustomers();
        this.model.SiteId = output.siteId;
      });
  }

  protected newAnalysisCompany() {
    void this.dialogSerivce
      .open({
        viewModel: AnalysisCompanyDialog,
        model: {},
        position: () => {
          return {
            top: 0,
          };
        },
      })
      .whenClosed(async ({ output }) => {
        if (!output?.analysisCompanyId) return;
        this.model.AnalysisCompanyId = output.analysisCompanyId;
      });
  }

  protected editCustomer() {
    if (!this.model.CustomerId) return;
    void this.dialogSerivce
      .open({
        viewModel: CustomerDialog,
        model: {
          customerId: this.model.CustomerId,
        },
        position: () => {
          return {
            top: 0,
          };
        },
      })
      .whenClosed(() => {});
  }

  @AsyncErrorHandler
  protected async getMooringStationInfo(id: number) {
    if (!id) return undefined;
    const stations = await this.mooringStationService.getAllCached();
    const mooringStation = stations.find((x) => x.Id == id);
    if (!mooringStation) return undefined;

    return {
      id: mooringStation.Id,
      title: mooringStation.Name,
    };
  }

  private mooringStations: MooringStationListItem[] = [];

  @AsyncErrorHandler
  protected async findMooringStation(searchText: string) {
    if (!this.mooringStations?.length) {
      this.mooringStations = await this.mooringStationService.getAll();
    }
    return this.mooringStations
      .filter((c) => c.Name.toLowerCase().includes(searchText.toLowerCase()))
      .map((c) => ({
        id: c.Id,
        title: c.Name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  protected mooringStationSelected(mooringStation: { id: number }) {
    this.model.ProductionLocationId = mooringStation.id;
  }

  protected togglePositioningMode() {
    this.createValidationRules();
  }

  protected toggleIsHorizontal() {
    this.model.SiteSetup.IsHorizontal = this.isHorizontal;
    this.createExampleGrid();
  }

  private createValidationRules() {
    this.ruleBuilder
      .with(this.model)
      .required('Title')
      .required('CustomerId')
      .required('SiteId')
      .required('AnalysisCompanyId')
      .required('ProductionLocationId')
      .min('Hs', 0)
      .max('Hs', 10)
      .min('Vc', 0)
      .max('Vc', 10)
      .done();

    if (this.hasOnlyFleet) {
      const mesage = '';

      this.ruleBuilder
        .with(this.model.SiteSetup)
        .custom('CenterPositionLat', mesage, validateIsDmsPosition)
        .custom('CenterPositionLong', mesage, validateIsDmsPosition);
    } else {
      const mesage = '';

      const options = this.ruleBuilder
        .with(this.model.SiteSetup)
        .required('NoCagesHeight')
        .min('NoCagesHeight', 0)
        .max('NoCagesHeight', 20)
        .required('NoCagesWidth')
        .min('NoCagesWidth', 0)
        .max('NoCagesWidth', 20)
        .required('GridLineLength')
        .min('GridLineLength', 1)
        .max('GridLineLength', 1000)
        .required('MooringDepth')
        .min('MooringDepth', 1)
        .max('MooringDepth', 1000)
        .required('NoAnchorLines')
        .min('NoAnchorLines', 1)
        .max('NoAnchorLines', 150)
        .required('NoBridles')
        .min('NoBridles', 1)
        .max('NoBridles', 4)
        // Locations
        .custom('CenterPositionLat', mesage, validateIsDmsPosition)
        .custom('CenterPositionLong', mesage, validateIsDmsPosition);

      if (this.useRotationMode) {
        // Rotation mode: require angle
        options.required('SiteAngleFromNorth').min('SiteAngleFromNorth', 0).max('SiteAngleFromNorth', 360);
      } else {
        // Manual mode: require corner positions
        options
          .custom('NWPositionLat', mesage, validateIsDmsPosition)
          .custom('NWPositionLong', mesage, validateIsDmsPosition)
          .custom('NEPositionLat', mesage, validateIsDmsPosition)
          .custom('NEPositionLong', mesage, validateIsDmsPosition)
          .custom('SWPositionLat', mesage, validateIsDmsPosition)
          .custom('SWPositionLong', mesage, validateIsDmsPosition)
          .custom('SEPositionLat', mesage, validateIsDmsPosition)
          .custom('SEPositionLong', mesage, validateIsDmsPosition);
      }

      if (this.model.SiteSetup.HasServiceCorridor) {
        options
          .required('ServiceCorridorPlacement')
          .min('ServiceCorridorPlacement', 1)
          .max('ServiceCorridorPlacement', 20)
          .required('ServiceCorridorWidth')
          .min('ServiceCorridorWidth', 1)
          .max('ServiceCorridorWidth', 1000);
      }

      options.done();
    }
  }

  protected async getconfirmChangeWithConsequencesText(offerId: number) {
      let confirmChangeWithConsequencesText = this.t.tr('mooring.confirmChangeWithConsequences');
      confirmChangeWithConsequencesText += '<br><br>';

      const mooringOfferChangeDifferenceDto: MooringOfferChangeDifferenceDto = {
        NoCagesWidthOld: this.originalValues.NoCagesWidthOld,
        NoCagesWidthNew: this.model.SiteSetup.NoCagesWidth,
        NoCagesHeightOld: this.originalValues.NoCagesHeightOld,
        NoCagesHeightNew: this.model.SiteSetup.NoCagesHeight,
        NoBridlesOld: this.originalValues.NoBridlesOld,
        NoBridlesNew: this.model.SiteSetup.NoBridles,
        NoAnchorLinesOld: this.originalValues.NoAnchorLinesOld,
        NoAnchorLinesNew: this.model.SiteSetup.NoAnchorLines,
      };

      const response  = await this.mooringOfferService.previewOfferChanges(offerId, mooringOfferChangeDifferenceDto);
      const mooringOfferChangeImpactDto: MooringOfferChangeImpactDto = response.data;

      
      if (mooringOfferChangeImpactDto.AnchorLinesAdded > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeAnchorLinesAdd', {x: mooringOfferChangeImpactDto.AnchorLinesAdded});
      }

      if (mooringOfferChangeImpactDto.AnchorLinesRemoved > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeAnchorLinesRemove', {x: mooringOfferChangeImpactDto.AnchorLinesRemoved});
      }

      if (mooringOfferChangeImpactDto.GridLinesAdded > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeGridLinesAdd', {x: mooringOfferChangeImpactDto.GridLinesAdded});
      }

      if (mooringOfferChangeImpactDto.GridLinesRemoved > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeGridLinesRemove', {x: mooringOfferChangeImpactDto.GridLinesRemoved});
      }

      if (mooringOfferChangeImpactDto.BuoysAdded > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeBuoysAdd', {x: mooringOfferChangeImpactDto.BuoysAdded});
      }

      if (mooringOfferChangeImpactDto.BuoysRemoved > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeBuoysRemove', {x: mooringOfferChangeImpactDto.BuoysRemoved});
      }

      if (mooringOfferChangeImpactDto.CagesAdded > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeFloatersAdd', {x: mooringOfferChangeImpactDto.CagesAdded});
      }

      if (mooringOfferChangeImpactDto.CagesRemoved > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeFloatersRemoved', {x: mooringOfferChangeImpactDto.CagesRemoved});
      }

      if (mooringOfferChangeImpactDto.BridlesAdded > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeBridlesAdd' , {x: mooringOfferChangeImpactDto.BridlesAdded});
      }

      if (mooringOfferChangeImpactDto.BridlesRemoved > 0) {
        confirmChangeWithConsequencesText += "<br>";
        confirmChangeWithConsequencesText += this.t.tr('mooring.changeBridlesRemove' , {x: mooringOfferChangeImpactDto.BridlesRemoved});
      }


      return confirmChangeWithConsequencesText;
  }
}

const setCleanDmsFormat = <T extends object>(key: keyof T, obj: T) => {
  const value = obj[key] as string | undefined;
  if (!value) return;
  obj[key as string] = cleanDmsFormat(value);
};

const validateIsDmsPosition = (value?: string | unknown) => {
  if (!value || typeof value !== 'string') return true;
  return isDmsGpsPosition(value);
};

// const createTest = () => {
//   const model = new CreateMooringOfferDto();
//   model.Title = 'Testing 1';
//   model.CustomerId = 503;
//   model.ContactId = 369;
//   model.SiteId = 1884;
//   model.SalesPersonId = '03cdfed6-5774-44c7-a173-3ca6f94fe0de';
//   model.SiteId = 1;
//   model.Hs = 2.5;
//   model.Vc = 0.5;
//   model.AnalysisCompanyId = 1;
//   model.ProductionLocationId = 1;
//   model.SiteSetup = {
//     FleetAngleFromNorth: 0.0,
//     FleetCenterPositionLat: null,
//     FleetCenterPositionLong: null,
//     CenterPositionLat: '59°20.804',
//     CenterPositionLong: '5°53.610',
//     NWPositionLat: '59°20.804',
//     NWPositionLong: '5°53.610',
//     NEPositionLat: '59°20.728',
//     NEPositionLong: '5°54.006',
//     SEPositionLat: '59°20.526',
//     SEPositionLong: '5°53.858',
//     SWPositionLat: '59°20.602',
//     SWPositionLong: '5°53.461',
//     // SWPositionLat: '',
//     // SWPositionLong: '',
//     NoCagesWidth: 4,
//     NoCagesHeight: 2,
//     NoAnchorLines: 22,
//     NoFleetAnchorLines: undefined,
//     NoBridles: 3,
//     GridLineLength: 100.0,
//     MooringDepth: 10.0,
//     HasServiceCorridor: true,
//     ServiceCorridorDirection: MooringServiceCorridorDirection.Horizontal,
//     ServiceCorridorPlacement: 2,
//     ServiceCorridorWidth: 200,
//     ReliabilityClass: 2,
//     StartBuoyPosition: 'NW',
//   };

//   return model;
// };
